import { useMutation, useQueryClient } from '@tanstack/react-query'
import { getTipHistory, sendTips } from 'api/worker'
import { useAuthContext } from 'components/auth'
import { useOptimisticUpdate } from 'hooks/useOptimisticUpdate'
import { MutationOptions, QueryFnData } from 'queries/types'
import { workerKeys } from './keys'

type TipHistory = QueryFnData<typeof getTipHistory>
type SendTipVariables = {
  tips: {
    workId: number
    amount: number
  }[]
}

export function useSendTipsMutation(
  filters: { workerId?: number; shiftId?: number },
  options: MutationOptions<typeof sendTips, [TipHistory], SendTipVariables> = {}
) {
  const queryClient = useQueryClient()
  const { company } = useAuthContext()

  return useMutation({
    ...useOptimisticUpdate(
      {
        ...options,
        onSuccess: (data, variables, context) => {
          queryClient.cancelQueries({
            queryKey: workerKeys.tipHistory(filters),
          })
          queryClient.setQueryData(workerKeys.tipHistory(filters), data)
          options.onSuccess?.(data, variables, context)
        },
      },
      [
        {
          queryKey: workerKeys.tipHistory(filters),
          updater: (data, previous) => {
            if (previous === undefined) {
              return previous
            }

            if (previous.tipTakeRate === undefined) {
              return previous
            }
            const tipAmount = data.tips?.reduce(
              (acc, tip) => acc + tip.amount,
              0
            )

            return {
              ...previous,
              totalPaymentAmount: previous.totalPaymentAmount + tipAmount,
              totalChargeAmount:
                previous.totalChargeAmount +
                tipAmount * (1 + previous.tipTakeRate / 100),
              workHistory: previous.workHistory.map((item) => {
                const didUpdate = data.tips?.find(
                  (tip) => tip.workId == item.work.workId
                )

                if (!didUpdate) {
                  return item
                }

                return {
                  ...item,
                  paymentHistory: [
                    {
                      userPaymentId: 0,
                      amount: didUpdate.amount,
                      createdAt: new Date().toISOString(),
                    } as const,
                    ...item.paymentHistory,
                  ],
                }
              }),
            }
          },
        },
      ]
    ),
    mutationFn: (data: SendTipVariables) => {
      if (!company?.id) {
        throw new Error('Company not found')
      }

      return sendTips({
        tips: data.tips,
        companyId: company.id,
      })
    },
  })
}
